import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Calendar,
  Download,
  RefreshCw,
  Filter,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Package,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Globe,
  Users,
  Building2,
  Briefcase,
  FileWarning,
  CheckCircle,
  ShieldCheck,
  ShieldOff,
  Settings,
  Plus
} from 'lucide-react';
import { FirstSaleIgnoreListService } from './FirstSaleSettings';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'];
const VARIANCE_COLORS = {
  negative: '#10b981', // green for savings
  positive: '#ef4444'  // red for overspend
};

// Error type definitions
const ERROR_TYPES = [
  { id: 'fs', label: 'FS/FS3 Terms', description: 'FOB Variance > -10%', color: 'blue', filter: (v, terms) => (terms.includes('FS') || terms.includes('FS3')) && v > -10 },
  { id: 'wi', label: 'WI/W3 Terms', description: 'FOB Variance < -4%', color: 'orange', filter: (v, terms) => (terms.includes('WI') || terms.includes('W3')) && v < -4 },
  { id: 'overvalued', label: 'Over-Valued', description: 'Custom > FOB by 3%+', color: 'red', filter: (v) => v > 3 },
  { id: 'verylow', label: 'Very Low', description: 'Custom < 75% of FOB', color: 'purple', filter: (v) => v < -25 },
  { id: 'mismatch', label: 'Record Mismatch', description: 'Prod vs Ship count differs', color: 'gray' }
];

// BigQuery Cost Service
class BigQueryCostService {
  constructor() {
    this.functions = null;
    this.auth = null;
  }

  initialize() {
    if (!this.functions) {
      this.functions = getFunctions();
    }
  }

  async waitForAuth() {
    const { getAuth } = await import('firebase/auth');
    return new Promise((resolve, reject) => {
      const auth = getAuth();
      const unsubscribe = auth.onAuthStateChanged(user => {
        unsubscribe();
        if (user) resolve(user);
        else reject(new Error('Not authenticated'));
      }, reject);
    });
  }

  async getCostAllocationData(filters = {}, startDate = null, endDate = null) {
    try {
      await this.waitForAuth();
      this.initialize();
      const getCostData = httpsCallable(this.functions, 'getCostAllocationWithDates', { timeout: 330000 });
      const result = await getCostData({
        startDate: startDate || '2025-07-01',
        endDate: endDate || '2025-11-30',
        filters: filters || {}
      });
      return result.data?.data || result.data || [];
    } catch (error) {
      console.error('BigQuery query error:', error);
      throw error;
    }
  }
}

const BigQueryCostServiceInstance = new BigQueryCostService();

/**
 * First Sale Error Report - Redesigned to match Cost Allocation Report style
 */
export default function FirstSaleErrorReport({ onBackToDashboard, onNavigateToIgnoreList, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Ignore list state
  const [ignoreList, setIgnoreList] = useState([]);
  const [ignoreListLoading, setIgnoreListLoading] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);

  // Date range filters
  const [startDate, setStartDate] = useState('2025-07-01');
  const [endDate, setEndDate] = useState('2025-11-30');

  // Post-load filters (multi-select arrays)
  const [postFilters, setPostFilters] = useState({
    division: [],
    customer: [],
    office: []
  });

  // Selected error type
  const [selectedErrorType, setSelectedErrorType] = useState('wi');

  // Selected month from pivot
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Expanded states for hierarchical detail table
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());

  // Section visibility
  const [showCharts, setShowCharts] = useState(true);
  const [showPivot, setShowPivot] = useState(true);
  const [showDetail, setShowDetail] = useState(true);

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState(null);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get unique values for dropdowns
  const dropdownOptions = useMemo(() => {
    if (!data.length) return { divisions: [], customers: [], offices: [], countries: [] };
    const divisions = [...new Set(data.map(r => r.division_name).filter(Boolean))].sort();
    const customers = [...new Set(data.map(r => r.customer).filter(Boolean))].sort();
    const offices = [...new Set(data.map(r => r.office).filter(Boolean))].sort();
    const countries = [...new Set(data.map(r => r.country_of_origin).filter(Boolean))].sort();
    return { divisions, customers, offices, countries };
  }, [data]);

  // Apply post-load filters
  const filteredData = useMemo(() => {
    let result = data;
    if (postFilters.division.length > 0) {
      result = result.filter(r => postFilters.division.includes(r.division_name));
    }
    if (postFilters.customer.length > 0) {
      result = result.filter(r => postFilters.customer.includes(r.customer));
    }
    if (postFilters.office.length > 0) {
      result = result.filter(r => postFilters.office.includes(r.office));
    }
    return result;
  }, [data, postFilters]);

  // Group data by commercial invoice and calculate variance
  const invoiceData = useMemo(() => {
    const grouped = {};

    filteredData.forEach(item => {
      const invoice = item.commercial_invoice || 'N/A';
      if (!grouped[invoice]) {
        grouped[invoice] = {
          commercial_invoice: invoice,
          termsSet: new Set(),
          divisionsSet: new Set(),
          managersSet: new Set(),
          contractorsSet: new Set(),
          customersSet: new Set(),
          countriesSet: new Set(),
          productionNumbersSet: new Set(),
          shipmentNumbersSet: new Set(),
          warehousesSet: new Set(),
          hasRecordMismatch: false,
          productionRecordCount: 0,
          shippingRecordCount: 0,
          total_fob: 0,
          total_line_entered_value: 0,
          total_qty: 0,
          last_receipt_month: null,
          office: item.office || 'N/A',
          warehouse: item.warehouse || 'N/A',
          lines: []
        };
      }

      const g = grouped[invoice];
      const terms = item.terms2 || item.Terms || '';
      if (terms) g.termsSet.add(terms);
      if (item.division_name) g.divisionsSet.add(item.division_name);
      if (item.Production_Manager) g.managersSet.add(item.Production_Manager);
      if (item.contractor) g.contractorsSet.add(item.contractor);
      if (item.customer) g.customersSet.add(item.customer);
      if (item.country_of_origin) g.countriesSet.add(item.country_of_origin);
      
      const prodNum = item.production_number || '';
      if (prodNum) g.productionNumbersSet.add(prodNum);
      const shipNum = item.shipment_num || '';
      if (shipNum) g.shipmentNumbersSet.add(shipNum);
      const wh = item.warehouse || '';
      if (wh) g.warehousesSet.add(wh);
      if (item.po_completeness_status === 'RECORD COUNT MISMATCH') {
        g.hasRecordMismatch = true;
      }
      const prodCount = parseInt(item.production_record_count) || 0;
      const shipCount = parseInt(item.shipping_record_count) || 0;
      if (prodCount > g.productionRecordCount) g.productionRecordCount = prodCount;
      if (shipCount > g.shippingRecordCount) g.shippingRecordCount = shipCount;

      g.total_fob += parseFloat(item.total_fob) || 0;
      g.total_line_entered_value += parseFloat(item.total_line_entered_value) || 0;
      g.total_qty += parseFloat(item.qty) || 0;

      const itemMonth = item.last_receipt_month || '';
      if (itemMonth && (!g.last_receipt_month || itemMonth > g.last_receipt_month)) {
        g.last_receipt_month = itemMonth;
      }

      g.lines.push(item);
    });

    return Object.values(grouped).map(g => {
      const fobVariance = g.total_fob > 0
        ? ((g.total_line_entered_value - g.total_fob) / g.total_fob) * 100
        : 0;

      let receiptMonth = 'N/A';
      if (g.last_receipt_month) {
        const [year, month] = g.last_receipt_month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        receiptMonth = `${monthNames[parseInt(month, 10) - 1]} ${year}`;
      }

      return {
        ...g,
        terms: Array.from(g.termsSet).join(', '),
        termsArray: Array.from(g.termsSet),
        division: Array.from(g.divisionsSet).join(', '),
        production_manager: Array.from(g.managersSet).join(', '),
        contractor: Array.from(g.contractorsSet).join(', '),
        customer: Array.from(g.customersSet).join(', '),
        country_of_origin: Array.from(g.countriesSet).join(', '),
        productionNumbers: Array.from(g.productionNumbersSet),
        shipmentNumbers: Array.from(g.shipmentNumbersSet),
        warehouses: Array.from(g.warehousesSet),
        hasRecordMismatch: g.hasRecordMismatch,
        productionRecordCount: g.productionRecordCount,
        shippingRecordCount: g.shippingRecordCount,
        po_count: g.productionNumbersSet.size,
        fobVariance,
        receipt_month: receiptMonth
      };
    });
  }, [filteredData]);

  // Get active ignore list entries as a Set for quick lookup
  const activeIgnoreSet = useMemo(() => {
    const ignoreMap = new Map();
    const activeItems = ignoreList.filter(item => item.active !== false);
    console.log(`🔍 Building ignore set from ${activeItems.length} active items:`, activeItems.map(i => i.commercial_invoice));
    activeItems.forEach(item => {
        const invoice = item.commercial_invoice?.trim().toLowerCase();
        if (invoice) {
          // Store the error types this invoice should be ignored for
          const existing = ignoreMap.get(invoice) || new Set();
          existing.add(item.error_type);
          ignoreMap.set(invoice, existing);
        }
      });
    return ignoreMap;
  }, [ignoreList]);

  // Check if an invoice should be ignored for a specific error type
  const isInvoiceIgnored = useCallback((invoiceNumber, errorType) => {
    const normalizedInvoice = invoiceNumber?.trim().toLowerCase();
    const ignoredTypes = activeIgnoreSet.get(normalizedInvoice);
    if (!ignoredTypes) return false;
    return ignoredTypes.has('all') || ignoredTypes.has(errorType);
  }, [activeIgnoreSet]);

  // Filter invoices by selected error type (with ignore list applied)
  const errorTypeInvoices = useMemo(() => {
    const errorType = ERROR_TYPES.find(e => e.id === selectedErrorType);
    if (!errorType) return [];

    return invoiceData.filter(inv => {
      // First check if this invoice matches the error type criteria
      let matchesError = false;
      if (selectedErrorType === 'fs') {
        matchesError = (inv.termsArray.includes('FS') || inv.termsArray.includes('FS3')) && inv.fobVariance > -10;
      } else if (selectedErrorType === 'wi') {
        matchesError = (inv.termsArray.includes('WI') || inv.termsArray.includes('W3')) && inv.fobVariance < -4;
      } else if (selectedErrorType === 'overvalued') {
        matchesError = inv.fobVariance > 3;
      } else if (selectedErrorType === 'verylow') {
        matchesError = inv.fobVariance < -25;
      } else if (selectedErrorType === 'mismatch') {
        matchesError = inv.hasRecordMismatch;
      }

      if (!matchesError) return false;

      // If showIgnored is true, show everything; otherwise filter out ignored invoices
      if (showIgnored) return true;
      return !isInvoiceIgnored(inv.commercial_invoice, selectedErrorType);
    });
  }, [invoiceData, selectedErrorType, isInvoiceIgnored, showIgnored]);

  // Count of ignored invoices for current error type
  const ignoredCount = useMemo(() => {
    return invoiceData.filter(inv => {
      let matchesError = false;
      if (selectedErrorType === 'fs') {
        matchesError = (inv.termsArray.includes('FS') || inv.termsArray.includes('FS3')) && inv.fobVariance > -10;
      } else if (selectedErrorType === 'wi') {
        matchesError = (inv.termsArray.includes('WI') || inv.termsArray.includes('W3')) && inv.fobVariance < -4;
      } else if (selectedErrorType === 'overvalued') {
        matchesError = inv.fobVariance > 3;
      } else if (selectedErrorType === 'verylow') {
        matchesError = inv.fobVariance < -25;
      } else if (selectedErrorType === 'mismatch') {
        matchesError = inv.hasRecordMismatch;
      }
      return matchesError && isInvoiceIgnored(inv.commercial_invoice, selectedErrorType);
    }).length;
  }, [invoiceData, selectedErrorType, isInvoiceIgnored]);

  // Apply month filter
  const monthFilteredInvoices = useMemo(() => {
    if (!selectedMonth) return errorTypeInvoices;
    return errorTypeInvoices.filter(inv => inv.last_receipt_month === selectedMonth);
  }, [errorTypeInvoices, selectedMonth]);

  // Pivot data (grouped by month)
  const pivotData = useMemo(() => {
    const grouped = {};

    errorTypeInvoices.forEach(inv => {
      const month = inv.last_receipt_month || 'Unknown';
      if (!grouped[month]) {
        grouped[month] = {
          month,
          invoice_count: 0,
          total_fob: 0,
          total_custom_value: 0,
          total_variance_amount: 0
        };
      }
      grouped[month].invoice_count += 1;
      grouped[month].total_fob += inv.total_fob;
      grouped[month].total_custom_value += inv.total_line_entered_value;
      grouped[month].total_variance_amount += inv.total_line_entered_value - inv.total_fob;
    });

    return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
  }, [errorTypeInvoices]);

  // Grand totals
  const grandTotals = useMemo(() => {
    return pivotData.reduce((acc, row) => ({
      invoice_count: acc.invoice_count + row.invoice_count,
      total_fob: acc.total_fob + row.total_fob,
      total_custom_value: acc.total_custom_value + row.total_custom_value,
      total_variance_amount: acc.total_variance_amount + row.total_variance_amount
    }), { invoice_count: 0, total_fob: 0, total_custom_value: 0, total_variance_amount: 0 });
  }, [pivotData]);

  // Error type counts (excluding ignored invoices)
  const errorCounts = useMemo(() => {
    const counts = {};
    ERROR_TYPES.forEach(et => {
      counts[et.id] = invoiceData.filter(inv => {
        // First check if matches error criteria
        let matchesError = false;
        if (et.id === 'fs') matchesError = (inv.termsArray.includes('FS') || inv.termsArray.includes('FS3')) && inv.fobVariance > -10;
        else if (et.id === 'wi') matchesError = (inv.termsArray.includes('WI') || inv.termsArray.includes('W3')) && inv.fobVariance < -4;
        else if (et.id === 'overvalued') matchesError = inv.fobVariance > 3;
        else if (et.id === 'verylow') matchesError = inv.fobVariance < -25;
        else if (et.id === 'mismatch') matchesError = inv.hasRecordMismatch;

        if (!matchesError) return false;

        // Exclude ignored invoices from the count
        return !isInvoiceIgnored(inv.commercial_invoice, et.id);
      }).length;
    });
    return counts;
  }, [invoiceData, isInvoiceIgnored]);

  // Country distribution chart data
  const countryData = useMemo(() => {
    const grouped = {};
    monthFilteredInvoices.forEach(inv => {
      const country = inv.country_of_origin || 'Unknown';
      if (!grouped[country]) grouped[country] = 0;
      grouped[country] += inv.total_fob;
    });
    return Object.entries(grouped)
      .map(([name, value], idx) => ({ name, value, color: COLORS[idx % COLORS.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [monthFilteredInvoices]);

  // Variance by month stacked bar data
  const varianceByMonthData = useMemo(() => {
    return pivotData.map(row => ({
      month: row.month,
      'Variance': row.total_variance_amount
    }));
  }, [pivotData]);

  // Customer impact data
  const customerImpactData = useMemo(() => {
    const grouped = {};
    monthFilteredInvoices.forEach(inv => {
      const customer = inv.customer || 'Unknown';
      if (!grouped[customer]) grouped[customer] = 0;
      grouped[customer] += inv.total_line_entered_value - inv.total_fob;
    });
    return Object.entries(grouped)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        value: Math.round(value)
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 10);
  }, [monthFilteredInvoices]);

  // Hierarchical detail data
  const hierarchicalData = useMemo(() => {
    return monthFilteredInvoices.map(inv => {
      const poMap = new Map();

      inv.lines.forEach(line => {
        const po = line.production_number || 'Unknown';
        if (!poMap.has(po)) {
          poMap.set(po, {
            po,
            shipmentNumbersSet: new Set(),
            styles: [],
            totals: { qty: 0, fob: 0, custom_value: 0 }
          });
        }

        const poData = poMap.get(po);
        const shipNum = line.shipment_num || '';
        if (shipNum) poData.shipmentNumbersSet.add(shipNum);
        const qty = parseFloat(line.qty) || 0;
        const fob = parseFloat(line.total_fob) || 0;
        const customValue = parseFloat(line.total_line_entered_value) || 0;

        poData.styles.push({
          style: line.style,
          color: line.color,
          qty,
          fob,
          custom_value: customValue,
          variance: fob > 0 ? ((customValue - fob) / fob) * 100 : 0
        });

        poData.totals.qty += qty;
        poData.totals.fob += fob;
        poData.totals.custom_value += customValue;
      });

      return {
        ...inv,
        pos: Array.from(poMap.values()).map(p => ({
          ...p,
          shipmentNumbers: Array.from(p.shipmentNumbersSet)
        }))
      };
    });
  }, [monthFilteredInvoices]);

  // Load ignore list
  const loadIgnoreList = useCallback(async () => {
    try {
      setIgnoreListLoading(true);
      const list = await FirstSaleIgnoreListService.getIgnoreList();
      setIgnoreList(Array.isArray(list) ? list : []);
      console.log(`✅ Loaded ${(list || []).length} ignored invoices`);
    } catch (err) {
      console.error('❌ Error loading ignore list:', err);
      // Don't set error - ignore list failure shouldn't block the report
      setIgnoreList([]);
    } finally {
      setIgnoreListLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedMonth(null);
      setExpandedInvoices(new Set());
      setExpandedPOs(new Set());

      // Load both data and ignore list in parallel
      const [allData] = await Promise.all([
        BigQueryCostServiceInstance.getCostAllocationData({}, startDate, endDate),
        loadIgnoreList()
      ]);
      setData(allData || []);
      console.log(`✅ Loaded ${(allData || []).length} records`);
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, loadIgnoreList]);

  useEffect(() => {
    fetchData();
  }, []);

  const toggleInvoice = (invoice) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(invoice)) next.delete(invoice);
      else next.add(invoice);
      return next;
    });
  };

  const togglePO = (key) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleMultiSelect = (filterType, value) => {
    setPostFilters(prev => {
      const current = prev[filterType] || [];
      if (current.includes(value)) {
        return { ...prev, [filterType]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [filterType]: [...current, value] };
      }
    });
  };

  const clearFilter = (filterType) => {
    setPostFilters(prev => ({ ...prev, [filterType]: [] }));
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = pivotData.map(row => ({
      'Month': row.month,
      'Invoice Count': row.invoice_count,
      'Total FOB': row.total_fob,
      'Total Custom Value': row.total_custom_value,
      'Variance Amount': row.total_variance_amount,
      'Variance %': row.total_fob > 0 ? ((row.total_variance_amount / row.total_fob) * 100).toFixed(2) + '%' : 'N/A'
    }));
    summaryData.push({
      'Month': 'Grand Total',
      'Invoice Count': grandTotals.invoice_count,
      'Total FOB': grandTotals.total_fob,
      'Total Custom Value': grandTotals.total_custom_value,
      'Variance Amount': grandTotals.total_variance_amount,
      'Variance %': grandTotals.total_fob > 0 ? ((grandTotals.total_variance_amount / grandTotals.total_fob) * 100).toFixed(2) + '%' : 'N/A'
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

    // Detail sheet
    const detailData = monthFilteredInvoices.map(inv => ({
      'Commercial Invoice': inv.commercial_invoice,
      'Receipt Month': inv.receipt_month,
      'Terms': inv.terms,
      'Division': inv.division,
      'Customer': inv.customer,
      'Production Manager': inv.production_manager,
      'Country': inv.country_of_origin,
      'PO Count': inv.po_count,
      'Total FOB': inv.total_fob,
      'Total Custom Value': inv.total_line_entered_value,
      'FOB Variance %': inv.fobVariance.toFixed(2) + '%',
      'Warehouse': inv.warehouses.join(', '),
      'Prod Lines': inv.productionRecordCount,
      'Ship Lines': inv.shippingRecordCount,
      'Record Mismatch': inv.hasRecordMismatch ? 'YES' : '',
      'Shipment Numbers': inv.shipmentNumbers.join(', '),
      'Production Numbers': inv.productionNumbers.join(', ')
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailData), 'Invoices');

    const errorType = ERROR_TYPES.find(e => e.id === selectedErrorType);
    XLSX.writeFile(wb, `First_Sale_${errorType?.label || 'Report'}_${startDate}_to_${endDate}.xlsx`);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);
    return value < 0 ? `(${formatted})` : formatted;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || entry.fill }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const MultiSelectDropdown = ({ label, icon: Icon, options, selected, filterType }) => {
    const isOpen = openDropdown === filterType;

    return (
      <div className="relative">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {Icon && <Icon size={14} className="inline mr-1" />}
          {label}
        </label>
        <button
          type="button"
          onClick={() => setOpenDropdown(isOpen ? null : filterType)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
        >
          <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
            {selected.length === 0 ? `All ${label}` : `${selected.length} selected`}
          </span>
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 border-b px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-gray-600">{selected.length} selected</span>
              <button onClick={() => clearFilter(filterType)} className="text-xs text-blue-600 hover:text-blue-800">Clear</button>
            </div>
            {options.map(option => (
              <label key={option} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleMultiSelect(filterType, option)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-gray-700 truncate">{option}</span>
              </label>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No options available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={embedded ? "bg-slate-50 p-4" : "p-6 bg-slate-50 min-h-screen"}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        )}
        <div className="flex items-center justify-between">
          {!embedded && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">First Sale Error Report</h1>
              <p className="text-sm text-gray-600">Records with FOB variance outside acceptable thresholds</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPivot(!showPivot)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showPivot ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Pivot
            </button>
            <button
              onClick={() => setShowCharts(!showCharts)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showCharts ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
            >
              <BarChart3 size={16} />
              Charts
            </button>
            <button
              onClick={() => setShowDetail(!showDetail)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showDetail ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Detail
            </button>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            {onNavigateToIgnoreList && (
              <button
                onClick={onNavigateToIgnoreList}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              >
                <Settings size={16} />
                Manage Ignore List
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            />
          </div>

          <MultiSelectDropdown
            label="Division"
            icon={Building2}
            options={dropdownOptions.divisions}
            selected={postFilters.division}
            filterType="division"
          />

          <MultiSelectDropdown
            label="Customer"
            icon={Users}
            options={dropdownOptions.customers}
            selected={postFilters.customer}
            filterType="customer"
          />

          <MultiSelectDropdown
            label="Office"
            icon={Briefcase}
            options={dropdownOptions.offices}
            selected={postFilters.office}
            filterType="office"
          />
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Load Data'}
          </button>

          {selectedMonth && (
            <button
              onClick={() => setSelectedMonth(null)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
            >
              Clear Month Filter: {selectedMonth}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-sm font-medium text-red-900">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error Type Selector */}
      {!loading && data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {ERROR_TYPES.map(et => {
            const isSelected = selectedErrorType === et.id;
            const count = errorCounts[et.id] || 0;
            const colorClasses = {
              blue: isSelected ? 'bg-blue-100 border-blue-500 text-blue-900' : 'bg-white border-blue-200 text-blue-700 hover:bg-blue-50',
              orange: isSelected ? 'bg-orange-100 border-orange-500 text-orange-900' : 'bg-white border-orange-200 text-orange-700 hover:bg-orange-50',
              red: isSelected ? 'bg-red-100 border-red-500 text-red-900' : 'bg-white border-red-200 text-red-700 hover:bg-red-50',
              purple: isSelected ? 'bg-purple-100 border-purple-500 text-purple-900' : 'bg-white border-purple-200 text-purple-700 hover:bg-purple-50',
              gray: isSelected ? 'bg-gray-200 border-gray-500 text-gray-900' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            };

            return (
              <button
                key={et.id}
                onClick={() => { setSelectedErrorType(et.id); setSelectedMonth(null); }}
                className={`border-2 rounded-xl p-4 text-left transition-colors ${colorClasses[et.color]}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">{et.label}</span>
                  {isSelected && <CheckCircle size={16} />}
                </div>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs opacity-75">{et.description}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Ignored Invoices Toggle */}
      {!loading && data.length > 0 && ignoredCount > 0 && (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <div>
              <span className="text-sm font-medium text-emerald-900">
                {ignoredCount} invoice{ignoredCount !== 1 ? 's' : ''} ignored
              </span>
              <span className="text-sm text-emerald-700 ml-2">
                for {ERROR_TYPES.find(e => e.id === selectedErrorType)?.label}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowIgnored(!showIgnored)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              showIgnored
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
            }`}
          >
            {showIgnored ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
            {showIgnored ? 'Hide Ignored' : 'Show Ignored'}
          </button>
        </div>
      )}

      {/* Pivot Table */}
      {!loading && data.length > 0 && showPivot && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">📋 Monthly Summary - {ERROR_TYPES.find(e => e.id === selectedErrorType)?.label}</h3>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Month</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Invoices</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total FOB</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total Custom Value</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Variance Amount</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Variance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pivotData.map(row => {
                  const variancePct = row.total_fob > 0 ? (row.total_variance_amount / row.total_fob) * 100 : 0;
                  return (
                    <tr
                      key={row.month}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedMonth === row.month ? 'bg-blue-100' : ''}`}
                      onClick={() => setSelectedMonth(selectedMonth === row.month ? null : row.month)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                        {selectedMonth === row.month ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {row.month}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_fob)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_custom_value)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${row.total_variance_amount <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.total_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${variancePct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(variancePct)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-200 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{grandTotals.invoice_count}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_fob)}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_custom_value)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.total_variance_amount <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.total_variance_amount)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.total_fob > 0 && (grandTotals.total_variance_amount / grandTotals.total_fob) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {grandTotals.total_fob > 0 ? formatPercent((grandTotals.total_variance_amount / grandTotals.total_fob) * 100) : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {!loading && data.length > 0 && showCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Country Distribution */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe size={16} className="text-blue-600" />
              FOB by Country
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                >
                  {countryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Variance by Month */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-orange-600" />
              Variance by Month
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={varianceByMonthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Variance" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Customer Impact */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={16} className="text-purple-600" />
              Variance by Customer
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customerImpactData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Variance" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Hierarchical Detail Table */}
      {!loading && data.length > 0 && showDetail && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              📄 Invoice Details ({monthFilteredInvoices.length} invoices)
              {selectedMonth && <span className="ml-2 text-blue-600">- {selectedMonth}</span>}
            </h3>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 w-8"></th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice / PO / Style</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Month</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Terms</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Customer</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">PO Manager</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Country</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Warehouse</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Shipment</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Prod Lines</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Ship Lines</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">PO Count</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Total FOB</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Custom Value</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Variance %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hierarchicalData.slice(0, 100).map((inv) => (
                  <React.Fragment key={inv.commercial_invoice}>
                    {/* Invoice Row */}
                    <tr
                      className="bg-blue-50 hover:bg-blue-100 cursor-pointer font-medium"
                      onClick={() => toggleInvoice(inv.commercial_invoice)}
                    >
                      <td className="px-3 py-2">
                        {expandedInvoices.has(inv.commercial_invoice) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-2 text-blue-900">{inv.commercial_invoice}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.receipt_month}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.terms}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.customer}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.production_manager || '-'}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.country_of_origin}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.warehouses.join(', ') || '-'}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.shipmentNumbers.join(', ') || '-'}</td>
                      <td className={`px-3 py-2 text-right ${inv.hasRecordMismatch ? 'text-red-600 font-bold' : 'text-blue-800'}`}>{inv.productionRecordCount || '-'}</td>
                      <td className={`px-3 py-2 text-right ${inv.hasRecordMismatch ? 'text-red-600 font-bold' : 'text-blue-800'}`}>{inv.shippingRecordCount || '-'}</td>
                      <td className="px-3 py-2 text-right text-blue-800">{inv.po_count}</td>
                      <td className="px-3 py-2 text-right text-blue-900">{formatCurrency(inv.total_fob)}</td>
                      <td className="px-3 py-2 text-right text-blue-900">{formatCurrency(inv.total_line_entered_value)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${inv.fobVariance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(inv.fobVariance)}
                      </td>
                    </tr>

                    {/* PO Rows */}
                    {expandedInvoices.has(inv.commercial_invoice) && inv.pos.map((po) => (
                      <React.Fragment key={`${inv.commercial_invoice}-${po.po}`}>
                        <tr
                          className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                          onClick={() => togglePO(`${inv.commercial_invoice}-${po.po}`)}
                        >
                          <td className="px-3 py-2 pl-6">
                            {expandedPOs.has(`${inv.commercial_invoice}-${po.po}`) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </td>
                          <td className="px-3 py-2 text-gray-700 pl-4">↳ PO: {po.po}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-gray-600">{po.shipmentNumbers?.join(', ') || '-'}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right text-gray-600">{po.styles.length}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(po.totals.fob)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(po.totals.custom_value)}</td>
                          <td className={`px-3 py-2 text-right ${po.totals.fob > 0 && ((po.totals.custom_value - po.totals.fob) / po.totals.fob) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {po.totals.fob > 0 ? formatPercent(((po.totals.custom_value - po.totals.fob) / po.totals.fob) * 100) : '-'}
                          </td>
                        </tr>

                        {/* Style Rows */}
                        {expandedPOs.has(`${inv.commercial_invoice}-${po.po}`) && po.styles.map((style, idx) => (
                          <tr key={`${inv.commercial_invoice}-${po.po}-${idx}`} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-gray-500 pl-10">↳ {style.style || '-'} / {style.color || '-'}</td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatNumber(style.qty)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatCurrency(style.fob)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatCurrency(style.custom_value)}</td>
                            <td className={`px-3 py-1.5 text-right ${style.variance <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {formatPercent(style.variance)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hierarchicalData.length > 100 && (
            <div className="bg-yellow-50 border-t border-yellow-200 px-4 py-3">
              <p className="text-xs text-yellow-800">Showing first 100 of {hierarchicalData.length} invoices. Export to Excel to see all.</p>
            </div>
          )}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && data.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 font-medium">No data found</p>
          <p className="text-sm text-gray-500 mt-1">Click "Load Data" to fetch data for the selected date range</p>
        </div>
      )}
    </div>
  );
}
